import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../api';
import { MAUZAS } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShieldCheck, Users, Briefcase, Crown, FileText, UserPlus, CheckCircle2, XCircle, Phone, MapPin, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState({ jakrikras: 0, nokmas: 0, applicants: 0, applications: 0 });
  const [jakrikras, setJakrikras] = useState([]);
  const [nokmas, setNokmas] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openAdd, setOpenAdd] = useState(false);
  const [jakForm, setJakForm] = useState({ name: '', mobile: '', password: '', mauzaHandled: '' });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, j, n, a] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users', { params: { role: 'jakrikra' } }),
        api.get('/admin/users', { params: { role: 'nokma' } }),
        api.get('/applications'),
      ]);
      setStats(s.data);
      setJakrikras(j.data);
      setNokmas(n.data);
      setApps(a.data);
    } catch (e) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const pendingNokmas = nokmas.filter((u) => u.status === 'pending');
  const approvedNokmas = nokmas.filter((u) => u.status === 'approved');

  const addJakrikra = async () => {
    if (!jakForm.name || !jakForm.mobile || !jakForm.password || !jakForm.mauzaHandled) { toast.error('Fill all fields'); return; }
    if (!/^\d{10}$/.test(jakForm.mobile)) { toast.error('Valid 10-digit mobile required'); return; }
    try {
      await api.post('/admin/jakrikras', jakForm);
      toast.success('Jakrikra added');
      setJakForm({ name: '', mobile: '', password: '', mauzaHandled: '' });
      setOpenAdd(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const removeJakrikra = async (id) => {
    try {
      await api.delete(`/admin/jakrikras/${id}`);
      toast.success('Jakrikra removed');
      fetchAll();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const setNokmaStatus = async (id, status) => {
    try {
      await api.put(`/admin/nokmas/${id}/status`, { status });
      toast.success(`Nokma Sani ${status}`);
      fetchAll();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const statCards = [
    { l: 'Jakrikras', v: stats.jakrikras, icon: Briefcase, c: 'text-primary' },
    { l: 'Nokma Sanis', v: stats.nokmas, icon: Crown, c: 'text-accent' },
    { l: 'Applicants', v: stats.applicants, icon: Users, c: 'text-blue-600' },
    { l: 'Applications', v: stats.applications, icon: FileText, c: 'text-amber-600' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="text-sm text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Administrator</div>
          <h1 className="font-serif text-3xl font-bold mt-1">Council Administration</h1>
          <div className="text-sm text-muted-foreground mt-1">Manage Jakrikras, approve Nokma Sani registrations and oversee operations.</div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((s) => (
            <Card key={s.l} className="border-border">
              <CardContent className="pt-6 flex items-center gap-4">
                <div className={`h-12 w-12 rounded-lg bg-secondary flex items-center justify-center ${s.c}`}>
                  <s.icon className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{s.v}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">{s.l}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="nokma">
          <TabsList>
            <TabsTrigger value="nokma">Nokma Approvals {pendingNokmas.length > 0 && <Badge className="ml-2 bg-amber-100 text-amber-800 border-amber-200" variant="outline">{pendingNokmas.length}</Badge>}</TabsTrigger>
            <TabsTrigger value="jakrikra">Jakrikras ({jakrikras.length})</TabsTrigger>
            <TabsTrigger value="applications">All Applications ({apps.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="nokma" className="mt-4">
            <Card className="border-border">
              <CardHeader><CardTitle className="font-serif">Pending Nokma Sani Registrations</CardTitle></CardHeader>
              <CardContent>
                {pendingNokmas.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">No pending registrations</div>
                ) : (
                  <div className="space-y-3">
                    {pendingNokmas.map((n) => (
                      <div key={n.id} className="border border-border rounded-lg p-4 bg-card flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="font-serif text-lg font-semibold">{n.nokmaName}</div>
                          <div className="text-sm text-muted-foreground">Sani: {n.saniName} · {n.akhing}</div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                            <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{n.mobile}</span>
                            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{MAUZAS.find((m) => m.id === n.mauzaNumber)?.label} · {n.laskerArea}</span>
                            <span>Nokma No: {n.nokmaNumber}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setNokmaStatus(n.id, 'rejected')} className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50">
                            <XCircle className="h-4 w-4" /> Reject
                          </Button>
                          <Button size="sm" onClick={() => setNokmaStatus(n.id, 'approved')} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                            <CheckCircle2 className="h-4 w-4" /> Approve
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-8">
                  <div className="font-serif text-lg font-semibold mb-3">Approved Nokma Sanis</div>
                  {approvedNokmas.length === 0 ? <div className="text-muted-foreground text-sm">None yet</div> : (
                    <div className="grid md:grid-cols-2 gap-3">
                      {approvedNokmas.map((n) => (
                        <div key={n.id} className="border border-border rounded-lg p-4 bg-card">
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{n.nokmaName}</div>
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Approved</Badge>
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">{n.akhing} · {MAUZAS.find((m) => m.id === n.mauzaNumber)?.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="jakrikra" className="mt-4">
            <Card className="border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-serif">Office Assistants (Jakrikras)</CardTitle>
                <Dialog open={openAdd} onOpenChange={setOpenAdd}>
                  <DialogTrigger asChild>
                    <Button className="gap-2 bg-primary hover:bg-primary/90"><UserPlus className="h-4 w-4" /> Add Jakrikra</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-serif">Add Jakrikra</DialogTitle>
                      <DialogDescription>Set credentials and assign Mauza.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div><Label>Name</Label><Input value={jakForm.name} onChange={(e) => setJakForm({ ...jakForm, name: e.target.value })} placeholder="e.g. Jakrikra VI" className="mt-1.5" /></div>
                      <div><Label>Mobile</Label><Input maxLength={10} inputMode="numeric" value={jakForm.mobile} onChange={(e) => setJakForm({ ...jakForm, mobile: e.target.value.replace(/\D/g, '') })} className="mt-1.5" /></div>
                      <div><Label>Password</Label><Input type="password" value={jakForm.password} onChange={(e) => setJakForm({ ...jakForm, password: e.target.value })} className="mt-1.5" /></div>
                      <div><Label>Mauza Handled</Label>
                        <Select value={jakForm.mauzaHandled} onValueChange={(v) => setJakForm({ ...jakForm, mauzaHandled: v })}>
                          <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select Mauza" /></SelectTrigger>
                          <SelectContent>{MAUZAS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setOpenAdd(false)}>Cancel</Button>
                      <Button onClick={addJakrikra} className="bg-primary hover:bg-primary/90">Add</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {jakrikras.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">No Jakrikras yet. Add one to start.</div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-3">
                    {jakrikras.map((j) => (
                      <div key={j.id} className="border border-border rounded-lg p-4 bg-card">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-serif text-lg font-semibold">{j.name}</div>
                            <div className="text-sm text-muted-foreground mt-1">{MAUZAS.find((m) => m.id === j.mauzaHandled)?.label}</div>
                            <div className="text-xs text-muted-foreground mt-1">Mobile: {j.mobile}</div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => removeJakrikra(j.id)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="applications" className="mt-4">
            <Card className="border-border">
              <CardHeader><CardTitle className="font-serif">All Applications</CardTitle></CardHeader>
              <CardContent>
                {apps.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">No applications yet</div>
                ) : (
                  <div className="space-y-2">
                    {apps.map((a) => (
                      <div key={a.id} className="border border-border rounded-lg p-4 bg-card flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="font-mono text-xs">{a.id}</Badge>
                            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">{a.typeLabel}</Badge>
                          </div>
                          <div className="font-medium mt-1">{a.applicantName}</div>
                          <div className="text-sm text-muted-foreground">{a.akhing} · {MAUZAS.find((m) => m.id === a.mauzaNumber)?.label}</div>
                        </div>
                        <Badge variant="outline" className={
                          a.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          a.status === 'rejected' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                          a.status === 'forwarded_nokma' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          'bg-amber-50 text-amber-700 border-amber-200'
                        }>{a.status.replace(/_/g, ' ')}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default AdminDashboard;
